import { useTranslation } from 'react-i18next'
import { Shield, Loader2 } from 'lucide-react'
import AsyncSelect from 'react-select/async'
import UserAvatar from '@components/UserAvatar'
import Modal from '@components/ui/Modal'
import { PermissionsModalProps, PermissionLevel } from './PermissionsModal/types'
import { usePermissionsModal } from './PermissionsModal/usePermissionsModal'
import { CustomOption, CustomSingleValue, customSelectStyles, formatUserName } from './PermissionsModal/UserSelectComponents'
import PermissionsTable from './PermissionsModal/PermissionsTable'

export default function PermissionsModal({
  isOpen,
  item,
  onClose
}: PermissionsModalProps) {
  const { t } = useTranslation()

  const {
    isLoading,
    isSaving,
    permissionsData,
    newOwner,
    setNewOwner,
    previousOwnerAccess,
    setPreviousOwnerAccess,
    permissions,
    allPermissionUsers,
    loadUsers,
    handleAddUser,
    handlePermissionChange,
    handleRemoveUser,
    handleSaveChanges,
    handleClose
  } = usePermissionsModal(isOpen, item, onClose)

  if (!item) return null

  // Previous owner access level options (includes 'none')
  const previousOwnerAccessOptions: { value: PermissionLevel; label: string }[] = [
    { value: 'none', label: t('permissions.levels.none') },
    { value: 'read', label: t('permissions.levels.read') },
    { value: 'write', label: t('permissions.levels.write') },
    { value: 'admin', label: t('permissions.levels.admin') }
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={t('permissions.title')}
      subtitle={item.name}
      icon={<Shield className="w-6 h-6 text-primary" />}
      disabled={isSaving}
      footer={
        permissionsData && !isLoading ? (
          <>
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-ghost"
              disabled={isSaving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSaveChanges}
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('permissions.saving')}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  {t('permissions.saveChanges')}
                </>
              )}
            </button>
          </>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-base-content/70">{t('permissions.loading')}</p>
        </div>
      ) : permissionsData ? (
        <div className="space-y-6">
            {/* Owner Transfer Section */}
            <div className="card bg-base-200 p-4">
              <h4 className="font-semibold text-lg mb-4">{t('permissions.changeOwner.title')}</h4>

              <div className="space-y-4">
                {/* Current Owner */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">{t('permissions.changeOwner.currentOwner')}</span>
                  </label>
                  <div className="flex items-center gap-3 p-3 bg-base-100 rounded-lg">
                    <UserAvatar user={permissionsData.resource.owner} size="md" showTooltip={false} />
                    <div>
                      <div className="font-medium">{formatUserName(permissionsData.resource.owner)}</div>
                      <div className="text-sm text-base-content/70">{permissionsData.resource.owner.email}</div>
                    </div>
                  </div>
                </div>

                {/* New Owner Select */}
                <div>
                  <label className="label">
                    <span className="label-text font-medium">{t('permissions.changeOwner.newOwner')}</span>
                  </label>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadUsers}
                    value={newOwner}
                    onChange={setNewOwner}
                    placeholder={t('permissions.changeOwner.searchPlaceholder')}
                    noOptionsMessage={() => t('permissions.noResults')}
                    loadingMessage={() => t('permissions.searching')}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    classNames={{
                      menu: () => 'bg-base-100',
                      menuList: () => 'bg-base-100'
                    }}
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue
                    }}
                    isClearable
                  />
                </div>

                {/* Previous Owner Access Level */}
                {newOwner && (
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">{t('permissions.changeOwner.previousOwnerAccess')}</span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      value={previousOwnerAccess}
                      onChange={(e) => setPreviousOwnerAccess(e.target.value as PermissionLevel)}
                    >
                      {previousOwnerAccessOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Permissions Management Section */}
            <PermissionsTable
              allPermissionUsers={allPermissionUsers}
              permissions={permissions}
              loadUsers={loadUsers}
              handleAddUser={handleAddUser}
              handlePermissionChange={handlePermissionChange}
              handleRemoveUser={handleRemoveUser}
            />
          </div>
        ) : null}
    </Modal>
  )
}
